import { AdEnginePlugin, type Hooks } from "../../../../core/AdEnginePlugin";
import { loadScript, pushTrack } from "utils";
import { type FandomCmp, LoadTimeEvent } from "common-types";
import type { ConfiantPluginConfig, ConfiantCallback } from "./confiant";
import { isConsentGranted } from "../../../../helpers/isConsentGranted";

/**
 * ConfiantPlugin class integrates the Confiant brand safety vendor with AdEngine.
 *
 * Technical implementation documentation can be found in the Confiant upgrade AdEngine ticket.
 * @see {@link https://fandom.atlassian.net/browse/ADEN-8886}
 */
export class ConfiantPlugin extends AdEnginePlugin {
  private readonly scriptDomain = "cdn.confiant-integrations.net";
  private readonly propertyId = "d-aIf3ibf0cYxCLB1HTWfBQOFEA";
  private isLoaded = false;

  /**
   * Checks if the Confiant plugin is enabled based on the ICBM configuration.
   * @returns True if the plugin is enabled, false otherwise.
   */
  static isEnabled(config: ConfiantPluginConfig): boolean {
    return Boolean(config?.icConfiant);
  }

  /**
   * Tracks blocked ads and logs the blocking event.
   * The tracking functionality still needs to be implemented.
   * @param blockingType - The type of blocking.
   * @param blockingId - The ID of the blocking.
   * @param isBlocked - Indicates if the ad is blocked.
   * @param wrapperId - The ID of the wrapper.
   * @param tagId - The ID of the tag.
   * @param impressionData - The data related to the ad impression.
   */
  // eslint-disable-next-line max-params
  private readonly trackBlock: ConfiantCallback = (
    blockingType,
    blockingId,
    isBlocked,
    wrapperId,
    tagId,
    impressionData,
  ) => {
    if (impressionData && impressionData.dfp) {
      const slotName = impressionData.dfp.s;
      // eslint-disable-next-line no-console
      console.debug(`Blocked ad on ${slotName}`);
      // TODO: Implement the tracking logic
    }
  };

  /**
   * Loads the Confiant script asynchronously.
   * The isLoaded flag is updated when the script loads in successfully.
   */
  private async loadConfiantScript() {
    const confiantLibraryUrl = `//${this.scriptDomain}/${this.propertyId}/gpt_and_prebid/config.js`;
    try {
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "confiant_started",
        time: performance.now(),
      });
      await loadScript(confiantLibraryUrl);
      this.isLoaded = true;
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "confiant_loaded",
        time: performance.now(),
      });
    } catch (error) {
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "confiant_error_loading",
        time: performance.now(),
      });
      console.error("Failed to load Confiant script", error);
    }
  }

  /**
   * Sets up the Confiant window object and assigns the trackBlock callback.
   */
  private setupConfiantWindowObject() {
    window.confiant = window.confiant ?? {};
    window.confiant.callback = this.trackBlock;
  }

  /**
   * Initializes the Confiant plugin by setting up the window object and loading the script.
   * This won't run is the plugin is already loaded or if it is disabled.
   *
   * The isLoaded member variable is used to prevent multiple loads of the script, especially when consent is updated.
   */
  private init() {
    if (this.isLoaded) {
      return;
    }

    this.setupConfiantWindowObject();
    this.loadConfiantScript();
  }

  /**
   * Hooks for the Confiant plugin.
   */
  hooks: Hooks = {
    /**
     * Hook triggered when consent is updated.
     * @param fandomCmp - The consent parameters.
     */
    onConsentUpdated: (fandomCmp: FandomCmp) => {
      if (!isConsentGranted(fandomCmp)) {
        return;
      }

      this.init();
    },
  };
}
